/**
 * ModelFooterPreview Component for UI-Model-1
 *
 * Displays a preview of the model footer as the end user would see it.
 * Shows navigation buttons (Previous/Next) and description text.
 * Clickable to select footer section for configuration.
 *
 * Layout aligned with existing UI-Model FooterSection.tsx
 */

import React from 'react';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { HideEyeIcon, LeftArrow, RightArrow } from 'assests/GmsIcons';
import { DEFAULT_FOOTER_CONFIGURATION } from '../../types/api.types';

interface ModelFooterPreviewProps {
  id?: string;
}

export const ModelFooterPreview = ({ id }: ModelFooterPreviewProps) => {
  const baseId = id || 'model-footer-preview';
  const { state, dispatch } = useUIModelState();
  const footerConfig = state.model?.layout.footer?.configuration ?? DEFAULT_FOOTER_CONFIGURATION;
  const isSelected = state.editorState.selectedSection === 'footer';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch({
      type: 'SET_SELECTED_SECTION',
      payload: { section: 'footer' },
    });
  };

  // When footer is hidden, show overlay with eye icon
  if (!footerConfig.showFooter) {
    return (
      <div role="button"
        id={baseId}
        data-testid={baseId}
        onClick={handleClick}
        className={`tw-relative tw-py-3 tw-px-6 tw-mt-2 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-cursor-pointer tw-transition-all tw-border-2 tw-bg-surface-sunken ${
          isSelected
            ? 'tw-border-accent'
            : 'tw-border-transparent hover:tw-bg-surface-hover'
        }`}
      >
        <span className="tw-text-ink-secondary [&_path]:tw-stroke-current">
          <HideEyeIcon />
        </span>
        <span className="tw-text-ink-secondary tw-text-sm">Footer</span>
      </div>
    );
  }

  return (
    <div role="button"
      id={baseId}
      data-testid={baseId}
      onClick={handleClick}
      className={`tw-relative tw-py-2 tw-px-6 tw-mt-2 tw-flex tw-justify-between tw-items-center tw-h-auto tw-cursor-pointer tw-transition-all tw-border-2 tw-bg-surface tw-border-t tw-border-edge ${
        isSelected
          ? 'tw-border-accent'
          : 'tw-border-transparent hover:tw-bg-surface-hover'
      }`}
    >
      {/* Left: Previous Button */}
      <div id={`${baseId}-left`} className="tw-flex tw-items-center">
        {footerConfig.showPreviousButton && (
          <button
            id={`${baseId}-prev-btn`}
            data-testid={`${baseId}-prev-btn`}
            className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-box-border tw-cursor-pointer tw-text-sm tw-leading-5 tw-min-h-[36px] tw-min-w-[92px] tw-p-2 tw-py-1 tw-rounded tw-border tw-bg-surface tw-text-accent tw-border-accent hover:tw-bg-accent-tint"
          >
            {footerConfig.previousButtonShowIcon && (
              footerConfig.previousButtonIconUrl
                ? <img src={footerConfig.previousButtonIconUrl} alt="" className="tw-w-4 tw-h-4" />
                : <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center [&>svg]:tw-w-full [&>svg]:tw-h-full [&_path]:tw-stroke-current"><LeftArrow /></span>
            )}
            {footerConfig.previousButtonText}
          </button>
        )}
      </div>

      {/* Center: Description */}
      <div id={`${baseId}-center`} className="tw-flex-1 tw-text-center tw-px-4">
        {footerConfig.showDescription && footerConfig.descriptionText && (
          <span
            id={`${baseId}-description`}
            className="tw-text-sm tw-text-ink-secondary tw-truncate tw-block"
            title={footerConfig.descriptionText}
          >
            {footerConfig.descriptionText}
          </span>
        )}
      </div>

      {/* Right: Next Button */}
      <div id={`${baseId}-right`} className="tw-flex tw-items-center">
        {footerConfig.showNextButton && (
          <button
            id={`${baseId}-next-btn`}
            data-testid={`${baseId}-next-btn`}
            className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-box-border tw-cursor-pointer tw-text-sm tw-leading-5 tw-min-h-[36px] tw-min-w-[92px] tw-p-2 tw-py-1 tw-rounded tw-border tw-bg-surface tw-text-accent tw-border-accent hover:tw-bg-accent-tint"
          >
            {footerConfig.nextButtonShowIcon && (
              footerConfig.nextButtonIconUrl
                ? <img src={footerConfig.nextButtonIconUrl} alt="" className="tw-w-4 tw-h-4" />
                : <span className="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center [&>svg]:tw-w-full [&>svg]:tw-h-full [&_path]:tw-stroke-current"><RightArrow /></span>
            )}
            {footerConfig.nextButtonText}
          </button>
        )}
      </div>
    </div>
  );
};

export default ModelFooterPreview;
